import { useState, useMemo, useEffect } from "react";
import { COUNTRIES } from "@/lib/countries";
import { WeighToleranceBar } from "@/components/WeighToleranceBar";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Database, Plus, CheckCircle2, XCircle, Trash2, Pencil, Loader2, Wrench, Save, X, Factory,
  Printer, Scale,
} from "lucide-react";
import {
  listMasters, createMaster, updateMaster, approveMaster, rejectMaster, deleteMaster, seedMastersFromExisting,
  bridgeDownloadUrl,
  getDispatchTolerance, setDispatchTolerance,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { EmployeeKycFields, EmployeeKycDetail, KycRequestsPanel, EmployeeExcelBar } from "@/components/EmployeeKyc";

/* Dispatch weight-tolerance editor. Anyone can view the current value; only
 * users with the `dispatch.tolerance` module (director by default) can edit it.
 */
const DispatchToleranceCard = () => {
  const { hasModule } = useAuth();
  const canEdit = hasModule("dispatch.tolerance");
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["dispatch-tolerance"],
    queryFn: getDispatchTolerance,
    staleTime: 60_000,
  });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  useEffect(() => { if (data?.three_way_kg != null) setDraft(String(data.three_way_kg)); }, [data?.three_way_kg]);

  const saveMut = useMutation({
    mutationFn: (v) => setDispatchTolerance(v),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dispatch-tolerance"] });
      toast.success("Tolerance updated");
      setEditing(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Update failed"),
  });

  return (
    <div data-testid="dispatch-tolerance-card" className="bg-amber-50 border border-amber-200 rounded-sm p-3 flex items-start gap-3">
      <Scale className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] tracking-widest uppercase font-semibold text-amber-800">Dispatch · 3-way weight tolerance</p>
        <p className="text-xs text-slate-700 mt-0.5">
          If scanned, invoice net and Kanta-Parchi net differ by more than this many kg, the SO stays open and needs management approval before it closes.
        </p>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          {editing ? (
            <>
              <input
                data-testid="tolerance-input"
                type="number" step="0.1" min="0"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="h-9 w-28 px-2 border border-slate-300 rounded-sm text-sm font-mono bg-white"
              />
              <span className="text-xs text-slate-600">kg</span>
              <Button
                data-testid="tolerance-save"
                size="sm"
                onClick={() => saveMut.mutate(parseFloat(draft))}
                disabled={saveMut.isPending || !(parseFloat(draft) >= 0)}
                className="bg-slate-900 hover:bg-slate-800 text-white h-9"
              >
                {saveMut.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                Save
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="h-9">Cancel</Button>
            </>
          ) : (
            <>
              <span data-testid="tolerance-value" className="font-mono font-bold text-lg text-slate-900">
                {isLoading ? "…" : `± ${data?.three_way_kg ?? "—"} kg`}
              </span>
              {canEdit && (
                <Button data-testid="tolerance-edit" size="sm" variant="outline" onClick={() => setEditing(true)} className="h-8">
                  <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const MASTER_TABS = [
  { key: "rm_grade", label: "RM Grade" },
  { key: "sfg_sku", label: "SFG SKU" },
  { key: "fg_sku", label: "FG SKU" },
  { key: "supplier", label: "Suppliers" },
  { key: "customer", label: "Customers" },
  { key: "press", label: "Presses" },
  { key: "employee", label: "Employees" },
  { key: "store_category", label: "Store Category" },
  { key: "store_spec_type", label: "Store Spec Type" },
  { key: "store_spec_unit", label: "Store Spec Unit" },
  { key: "talent_function", label: "HR · Talent Pipeline Master" },
];

const STATUS_BADGES = {
  active: "bg-green-100 text-green-800 border-green-200",
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
};

// -----------------------------------------------------------------
// Press → FG SKU capability picker (used inside Add/Edit dialogs)
// -----------------------------------------------------------------
const PressFgSkuPicker = ({ selectedIds, onChange }) => {
  const { data: fgSkus = [], isLoading } = useQuery({
    queryKey: ["masters", "fg_sku"],
    queryFn: () => listMasters({ master_type: "fg_sku" }),
    staleTime: 30_000,
  });
  const active = fgSkus.filter((m) => m.active !== false);
  const toggle = (id) => {
    const next = new Set(selectedIds || []);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(Array.from(next));
  };
  return (
    <div className="border-t border-slate-100 pt-2">
      <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold flex items-center gap-1.5">
        <Factory className="h-3 w-3" /> This press can produce which FG SKUs?
      </label>
      <p className="text-[10px] text-slate-500 mt-0.5 mb-1">Leave empty = press accepts any SKU (default). Ticked SKUs = whitelist enforced by Production Planning.</p>
      {isLoading ? (
        <div className="flex justify-center py-3"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
      ) : active.length === 0 ? (
        <p className="text-xs text-slate-500 italic p-2">No FG SKUs defined yet. Add some under the FG SKU tab first.</p>
      ) : (
        <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-sm p-1.5 grid grid-cols-2 gap-1">
          {active.map((s) => (
            <label
              key={s.id}
              className="flex items-center gap-1.5 px-1.5 py-1 text-xs hover:bg-slate-50 rounded cursor-pointer"
              data-testid={`press-fg-sku-${s.id}`}
            >
              <input
                type="checkbox"
                checked={(selectedIds || []).includes(s.id)}
                onChange={() => toggle(s.id)}
              />
              <span className="font-mono truncate">{s.name}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const EmployeeDetailDialog = ({ master, onClose, canEdit, onEdit, canKyc }) => {
  // Read-only card mirroring every field the Edit dialog exposes for an employee.
  const meta = master.meta || {};
  const yesNo = (v) => (v ? "Yes" : "No");
  const val = (v) => (v === "" || v == null ? "—" : String(v));
  const Field = ({ label, value, mono = false, testId }) => (
    <div>
      <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">{label}</p>
      <p className={`mt-0.5 text-sm text-slate-900 ${mono ? "font-mono" : ""}`} data-testid={testId}>{value}</p>
    </div>
  );
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-40 flex items-center justify-center p-4">
      <div
        className="bg-white rounded-sm w-full max-w-2xl p-6 max-h-[92vh] overflow-y-auto"
        data-testid="employee-detail-dialog"
      >
        <div className="flex items-start justify-between mb-4 gap-3">
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">Employee</p>
            <h3 className="font-heading text-xl font-bold text-slate-900" data-testid="emp-detail-name">{master.name}</h3>
            {master.code && <p className="text-xs font-mono text-slate-500 mt-0.5">{master.code}</p>}
          </div>
          <div className="flex items-center gap-1.5">
            {canEdit && (
              <Button
                data-testid="emp-detail-edit-btn"
                size="sm"
                variant="outline"
                onClick={() => onEdit(master)}
                className="h-8"
              >
                <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
              </Button>
            )}
            <button onClick={onClose} data-testid="emp-detail-close" className="text-slate-400 hover:text-slate-900">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <section>
            <p className="text-[10px] tracking-[0.25em] uppercase text-slate-400 font-semibold mb-2">Identity</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Field label="Department" value={val(meta.department)} testId="emp-detail-department" />
              <Field label="Skill Grade" value={val(meta.skill_grade || meta.designation)} testId="emp-detail-skill-grade" />
              <Field label="Date of Hire" value={val(meta.date_of_hire)} mono />
              <Field label="Status" value={val(master.status)} />
              <Field label="Active" value={yesNo(master.active !== false)} />
              <Field label="Notes" value={val(master.notes)} />
            </div>
          </section>

          <section>
            <p className="text-[10px] tracking-[0.25em] uppercase text-slate-400 font-semibold mb-2">Payroll</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Field label="Monthly Salary (₹)" value={val(meta.monthly_basic_salary)} mono />
              <Field label="OT Eligible" value={yesNo(meta.ot_eligible)} testId="emp-detail-ot" />
              <Field label="ESI Eligible" value={yesNo(meta.esi_eligible)} testId="emp-detail-esi" />
              <Field label="PF Eligible" value={yesNo(meta.pf_eligible)} testId="emp-detail-pf" />
              <Field label="Bank Account" value={val(meta.bank_account)} mono />
            </div>
          </section>

          <section>
            <p className="text-[10px] tracking-[0.25em] uppercase text-slate-400 font-semibold mb-2">Contact & Approvals</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Field label="Phone" value={val(meta.phone)} mono />
              <Field label="Email" value={val(meta.email)} mono />
              <Field label="L1 Leave Approver" value={val(meta.l1_approver)} />
            </div>
          </section>
          {canKyc && <EmployeeKycDetail meta={meta} pendingFields={master.kyc_pending_fields || []} />}
        </div>
      </div>
    </div>
  );
};


const MasterRow = ({ m, masterType, isDirector, canEditEmployee, canKyc, canDeleteEmployee, onApprove, onReject, onDelete, onEdit, onOpen }) => {
  const meta = m.meta || {};
  const isRm = masterType === "rm_grade";
  const isSfg = masterType === "sfg_sku";
  const isFg = masterType === "fg_sku";
  const isEmployee = masterType === "employee";
  const isSkuLike = isRm || isSfg || isFg;
  const fmtNum = (v) => (v === "" || v === null || v === undefined ? "—" : v);
  const canModify = isEmployee ? (isDirector || canEditEmployee || canKyc) : isDirector;
  return (
    <tr data-testid={`master-row-${m.id}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
      <td className="px-3 py-2 font-mono text-sm text-slate-900 font-semibold">
        {isEmployee ? (
          <button
            type="button"
            data-testid={`open-employee-${m.id}`}
            onClick={() => onOpen?.(m)}
            className="text-left text-slate-900 font-semibold hover:text-slate-600 hover:underline underline-offset-2"
          >
            {m.name}
          </button>
        ) : (
          m.name
        )}
      </td>
      {isFg && (
        <td className="px-3 py-2 text-xs text-slate-700 font-mono">{fmtNum(meta.grade)}</td>
      )}
      {isRm && (
        <>
          <td className="px-3 py-2 text-xs text-slate-700 font-mono">{fmtNum(meta.grade)}</td>
          <td className="px-3 py-2 text-xs text-slate-700 font-mono">{fmtNum(meta.thickness_mm)}</td>
        </>
      )}
      {isSfg && (
        <>
          <td className="px-3 py-2 text-xs text-slate-700 font-mono">{fmtNum(meta.grade)}</td>
          <td className="px-3 py-2 text-xs text-slate-700 font-mono">{fmtNum(meta.thickness_mm)}</td>
          <td className="px-3 py-2 text-xs text-slate-700 font-mono">{fmtNum(meta.width_mm)}</td>
        </>
      )}
      {!isSkuLike && (
        <td className="px-3 py-2 text-xs text-slate-500 font-mono">{m.code || "—"}</td>
      )}
      <td className="px-3 py-2">
        <span data-testid={`master-status-${m.id}`} className={`inline-flex items-center text-[10px] tracking-wider uppercase font-semibold px-1.5 py-0.5 rounded-sm border ${
          m.status === "inactive" ? "bg-red-100 text-red-800 border-red-300" : (STATUS_BADGES[m.status] || "")
        }`}>
          {m.status}
        </span>
        {isEmployee && m.kyc_pending_fields?.length > 0 && (
          <span data-testid={`kyc-pending-badge-${m.id}`} className="ml-1 inline-flex text-[10px] tracking-wider uppercase font-semibold px-1.5 py-0.5 rounded-sm border bg-amber-50 text-amber-800 border-amber-200">KYC change pending</span>
        )}
      </td>
      {masterType === "employee" && (
        <>
          <td className="px-3 py-2 text-xs text-slate-600 font-mono">{(m.meta?.date_of_hire || m.meta?.join_date || "").slice(0, 10) || "—"}</td>
          <td className={`px-3 py-2 text-xs font-mono ${m.relieved_on ? "text-red-700 font-bold" : "text-slate-400"}`} data-testid={`relieved-on-${m.id}`}>
            {m.relieved_on ? m.relieved_on.slice(0, 10) : "—"}
          </td>
        </>
      )}
      <td className="px-3 py-2 text-xs text-slate-500">{m.proposed_by || "—"}</td>
      <td className="px-3 py-2 text-xs text-slate-400 font-mono">{(m.created_at || "").slice(0, 10)}</td>
      <td className="px-3 py-2 text-right space-x-1">
        {isDirector && m.status === "pending" && (
          <>
            <Button size="sm" variant="outline" data-testid={`approve-${m.id}`} onClick={() => onApprove(m.id)} className="text-green-700 border-green-200 hover:bg-green-50 h-8">
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve
            </Button>
            <Button size="sm" variant="outline" data-testid={`reject-${m.id}`} onClick={() => onReject(m.id)} className="text-red-700 border-red-200 hover:bg-red-50 h-8">
              <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
            </Button>
          </>
        )}
        {canModify && m.status !== "pending" && m.status !== "inactive" && (
          <>
            <Button size="sm" variant="ghost" data-testid={`edit-${m.id}`} onClick={() => onEdit(m)} className="h-8"><Pencil className="h-3.5 w-3.5" /></Button>
            {(!isEmployee || isDirector || canEditEmployee || canDeleteEmployee) && (
              <Button size="sm" variant="ghost" data-testid={`delete-${m.id}`} onClick={() => onDelete(m.id, m.name)} className="h-8 text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
            )}
          </>
        )}
      </td>
    </tr>
  );
};

const AddDialog = ({ open, onClose, masterType, isDirector, canKyc, onSubmit, submitting }) => {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [notes, setNotes] = useState("");
  const [meta, setMeta] = useState({});
  if (!open) return null;
  const isRmGrade = masterType === "rm_grade";
  const isSfgSku  = masterType === "sfg_sku";
  const isFgSku   = masterType === "fg_sku";
  const isSupplier = masterType === "supplier";
  const isEmployee = masterType === "employee";
  const isSkuLike = isRmGrade || isSfgSku || isFgSku;
  const updateMeta = (k, v) => setMeta((m) => ({ ...m, [k]: v }));

  const humanLabel = {
    rm_grade: "RM grade",
    sfg_sku: "SFG SKU",
    fg_sku: "FG SKU",
    supplier: "supplier",
    customer: "customer",
    press: "press",
    employee: "employee",
    store_category: "store category",
    store_spec_type: "spec type",
    store_spec_unit: "spec unit",
  }[masterType] || masterType;

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-40 flex items-center justify-center p-4">
      <div className={`bg-white rounded-sm w-full ${isEmployee ? "max-w-xl" : "max-w-md"} p-5 max-h-[92vh] overflow-y-auto`} data-testid="add-master-dialog">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg font-bold text-slate-900">Add {humanLabel}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X className="h-5 w-5" /></button>
        </div>
        {!isDirector && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm p-2 mb-3">
            You&apos;ll create this as <b>Pending</b>. A director must approve before it becomes usable.
          </p>
        )}
        <div className="space-y-3">
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">
              Name * {isRmGrade && "(e.g. 50C470)"}{isSfgSku && "(e.g. 50C470)"}{isFgSku && "(e.g. EI-114)"}
            </label>
            <input data-testid="add-master-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} className={`mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm ${isEmployee ? "" : "font-mono uppercase"}`} />
          </div>

          {isSkuLike && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Grade (num) *</label>
                <input data-testid="add-master-grade" value={meta.grade || ""} onChange={(e) => updateMeta("grade", e.target.value)} placeholder="470" className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono uppercase" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Thickness mm *</label>
                <input data-testid="add-master-thickness" type="number" step="0.01" value={meta.thickness_mm || ""} onChange={(e) => updateMeta("thickness_mm", parseFloat(e.target.value) || 0)} placeholder="0.5" className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono" />
              </div>
              {isSfgSku && (
                <div className="col-span-2">
                  <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Width mm *</label>
                  <input data-testid="add-master-width" type="number" step="0.1" value={meta.width_mm || ""} onChange={(e) => updateMeta("width_mm", parseFloat(e.target.value) || 0)} placeholder="114" className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono" />
                </div>
              )}
            </div>
          )}

          {isSupplier && (
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Supplier ID *</label>
              <input
                data-testid="add-master-supplier-id"
                value={meta.supplier_id || ""}
                onChange={(e) => updateMeta("supplier_id", e.target.value.toUpperCase())}
                placeholder="S-01"
                className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono uppercase"
              />
              <p className="text-[10px] text-slate-500 mt-1">Printed on RM &amp; SFG stickers so shop-floor labels never reveal the vendor name.</p>
            </div>
          )}

          {masterType === "customer" && (
            <div className="space-y-2 border-t border-slate-100 pt-2">
              <CountrySelect testId="add-master-country" value={meta.country} onChange={(v) => updateMeta("country", v)} />
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">GST number</label>
                <input value={meta.gst || ""} onChange={(e) => updateMeta("gst", e.target.value.toUpperCase())} placeholder="27AABCU9603R1ZM" maxLength={15} className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono uppercase" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Phone</label>
                <input value={meta.phone || ""} onChange={(e) => updateMeta("phone", e.target.value)} placeholder="+91 98…" className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Billing address</label>
                <textarea value={meta.address || ""} onChange={(e) => updateMeta("address", e.target.value)} rows={2} className="mt-1 w-full px-2 py-1 border border-slate-300 rounded-sm text-sm" />
              </div>
            </div>
          )}

          {masterType === "press" && (
            <PressFgSkuPicker
              selectedIds={meta.allowed_fg_sku_ids || []}
              onChange={(ids) => updateMeta("allowed_fg_sku_ids", ids)}
            />
          )}

          {masterType === "employee" && (
            <div className="space-y-2 border-t border-slate-100 pt-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Emp ID / Code</label>
                  <input data-testid="add-master-emp-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="EMP-001" className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono uppercase" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Department</label>
                  <select data-testid="add-master-emp-department" value={meta.department || ""} onChange={(e) => updateMeta("department", e.target.value)} className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm bg-white">
                    <option value="">Select…</option>
                    <option value="STAFF SRE">STAFF SRE</option>
                    <option value="OPERATOR SRE">OPERATOR SRE</option>
                    <option value="OPERATOR CONTRACTOR 1">OPERATOR CONTRACTOR 1</option>
                    <option value="OPERATOR CONTRACTOR 2">OPERATOR CONTRACTOR 2</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Skill Grade</label>
                  <select data-testid="add-master-emp-skill-grade" value={meta.skill_grade || ""} onChange={(e) => updateMeta("skill_grade", e.target.value)} className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm bg-white">
                    <option value="">— Select —</option>
                    <option value="Skilled">Skilled</option>
                    <option value="Semi-skilled">Semi-skilled</option>
                    <option value="Unskilled">Unskilled</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Date of hire</label>
                  <input data-testid="add-master-emp-join-date" type="date" value={meta.date_of_hire || meta.join_date || ""} onChange={(e) => { updateMeta("date_of_hire", e.target.value); updateMeta("join_date", e.target.value); }} className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Monthly Salary (₹)</label>
                  <input data-testid="add-master-emp-salary" type="number" step="1" value={meta.monthly_basic_salary || ""} onChange={(e) => updateMeta("monthly_basic_salary", parseFloat(e.target.value) || 0)} placeholder="25000" className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Bank a/c number</label>
                  <input data-testid="add-master-emp-account" value={meta.bank_account || ""} onChange={(e) => updateMeta("bank_account", e.target.value)} placeholder="9876543210" className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono" />
                </div>
                <div className="flex items-center gap-2 md:col-span-1 pt-1">
                  <input id="add-emp-esi-elig" type="checkbox" data-testid="add-master-emp-esi-eligible"
                    checked={!!meta.esi_eligible}
                    onChange={(e) => updateMeta("esi_eligible", e.target.checked)}
                    className="h-4 w-4 border-slate-400" />
                  <label htmlFor="add-emp-esi-elig" className="text-xs font-semibold text-slate-700">
                    Eligible for ESI
                  </label>
                </div>
                <div className="flex items-center gap-2 md:col-span-1 pt-1">
                  <input id="add-emp-pf-elig" type="checkbox" data-testid="add-master-emp-pf-eligible"
                    checked={!!meta.pf_eligible}
                    onChange={(e) => updateMeta("pf_eligible", e.target.checked)}
                    className="h-4 w-4 border-slate-400" />
                  <label htmlFor="add-emp-pf-elig" className="text-xs font-semibold text-slate-700">
                    Eligible for PF
                  </label>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Phone</label>
                  <input data-testid="add-master-emp-phone" value={meta.phone || ""} onChange={(e) => updateMeta("phone", e.target.value)} placeholder="+91 98…" className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Email</label>
                  <input data-testid="add-master-emp-email" value={meta.email || ""} onChange={(e) => updateMeta("email", e.target.value)} placeholder="ravi@company.com" className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm" />
                </div>
                <div className="flex items-center gap-2 md:col-span-2 pt-1">
                  <input id="add-emp-ot" type="checkbox" data-testid="add-master-emp-ot"
                    checked={!!meta.ot_eligible}
                    onChange={(e) => updateMeta("ot_eligible", e.target.checked)}
                    className="h-4 w-4 border-slate-400" />
                  <label htmlFor="add-emp-ot" className="text-xs font-semibold text-slate-700">
                    OT eligible — this employee is paid for overtime hours beyond shift end
                  </label>
                </div>
                <div className="flex items-center gap-2 md:col-span-2 pt-1">
                  <input id="add-emp-l1" type="checkbox" data-testid="add-master-emp-l1"
                    checked={!!meta.is_l1_approver}
                    onChange={(e) => updateMeta("is_l1_approver", e.target.checked)}
                    className="h-4 w-4 border-slate-400" />
                  <label htmlFor="add-emp-l1" className="text-xs font-semibold text-slate-700">
                    Mark as L1 leave approver (supervisor dropdown in the Leave form)
                  </label>
                </div>
                {canKyc ? (
                  <EmployeeKycFields meta={meta} onChange={updateMeta} isNew isDirector={isDirector} prefix="add-emp" />
                ) : (
                  <p data-testid="add-emp-kyc-blocked" className="col-span-2 text-xs text-red-800 bg-red-50 border border-red-200 rounded-sm p-2">
                    New employees need Address, Aadhaar (number + front/back photo) and Emergency contact. Ask the Director to tick &quot;Employee Personal &amp; KYC Details&quot; for you in ERP Users.
                  </p>
                )}
              </div>
            </div>
          )}

          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">
              {isSkuLike ? "Notes (optional)" : "Notes (optional)"}
            </label>
            <input data-testid="add-master-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button data-testid="add-master-submit" onClick={() => onSubmit({ name, code, notes, meta })} disabled={submitting || !name.trim() || (isEmployee && !canKyc)} className="bg-slate-900 hover:bg-slate-800 text-white">
            {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : <><Plus className="h-4 w-4 mr-1.5" /> {isDirector ? "Add" : "Propose"}</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

const CountrySelect = ({ value, onChange, testId }) => (
  <div>
    <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Country</label>
    <select data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm bg-white">
      <option value="">Select country…</option>
      {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
    </select>
  </div>
);

const EditDialog = ({ master, onClose, onSave, submitting, canKyc, isDirector, kycOnly }) => {
  const [name, setName] = useState(master.name || "");
  const [code, setCode] = useState(master.code || "");
  const [notes, setNotes] = useState(master.notes || "");
  const [active, setActive] = useState(master.active !== false);
  const [meta, setMeta] = useState(master.meta || {});
  const isPress = master.master_type === "press";
  const isEmployee = master.master_type === "employee";
  const isRmGrade = master.master_type === "rm_grade";
  const isSfgSku = master.master_type === "sfg_sku";
  const isFgSku = master.master_type === "fg_sku";
  const isSkuLike = isRmGrade || isSfgSku || isFgSku;
  const updMeta = (k, v) => setMeta((m) => ({ ...m, [k]: v }));
  const updateEmpMeta = (k, v) => setMeta((m) => ({ ...m, [k]: v }));
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-40 flex items-center justify-center p-4">
      <div className={`bg-white rounded-sm w-full ${isEmployee ? "max-w-xl" : "max-w-md"} p-5 max-h-[92vh] overflow-y-auto`} data-testid="edit-master-dialog">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg font-bold text-slate-900">Edit {master.master_type}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X className="h-5 w-5" /></button>
        </div>
        {kycOnly && (
          <p data-testid="edit-kyc-only-note" className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-sm p-2 mb-3">
            You can update only the Personal &amp; KYC details below. Other fields are read-only for you.
          </p>
        )}
        <fieldset disabled={kycOnly} className={`space-y-3 min-w-0 ${kycOnly ? "opacity-60" : ""}`}>
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Name *</label>
            <input data-testid="edit-master-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono uppercase" />
          </div>
          {isSkuLike ? (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Grade *</label>
                <input data-testid="edit-master-grade" value={meta.grade || ""} onChange={(e) => updMeta("grade", e.target.value)} placeholder="470" className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono uppercase" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Thickness mm *</label>
                <input data-testid="edit-master-thickness" type="number" step="0.01" value={meta.thickness_mm ?? ""} onChange={(e) => updMeta("thickness_mm", e.target.value === "" ? "" : parseFloat(e.target.value) || 0)} placeholder="0.5" className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono" />
              </div>
              {isSfgSku && (
                <div className="col-span-2">
                  <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Width mm *</label>
                  <input data-testid="edit-master-width" type="number" step="0.1" value={meta.width_mm ?? ""} onChange={(e) => updMeta("width_mm", e.target.value === "" ? "" : parseFloat(e.target.value) || 0)} placeholder="114" className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono" />
                </div>
              )}
            </div>
          ) : (
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Code</label>
              <input value={code} onChange={(e) => setCode(e.target.value)} className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono uppercase" />
            </div>
          )}
          <div>
            <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Notes</label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" data-testid="edit-master-active" checked={active} onChange={(e) => setActive(e.target.checked)} />
            Active (usable in new records)
          </label>
          {master.master_type === "customer" && (
            <CountrySelect testId="edit-master-country" value={meta.country} onChange={(v) => updMeta("country", v)} />
          )}
          {isPress && (
            <PressFgSkuPicker
              selectedIds={meta.allowed_fg_sku_ids || []}
              onChange={(ids) => setMeta((m) => ({ ...m, allowed_fg_sku_ids: ids }))}
            />
          )}
          {isEmployee && (
            <div className="space-y-2 border-t border-slate-100 pt-2 grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Department</label>
                <select data-testid="edit-master-emp-department" value={meta.department || ""} onChange={(e) => updateEmpMeta("department", e.target.value)} className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm bg-white">
                  <option value="">Select…</option>
                  <option value="STAFF SRE">STAFF SRE</option>
                  <option value="OPERATOR SRE">OPERATOR SRE</option>
                  <option value="OPERATOR CONTRACTOR 1">OPERATOR CONTRACTOR 1</option>
                  <option value="OPERATOR CONTRACTOR 2">OPERATOR CONTRACTOR 2</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Skill Grade</label>
                <select data-testid="edit-master-emp-skill-grade" value={meta.skill_grade || meta.designation || ""} onChange={(e) => updateEmpMeta("skill_grade", e.target.value)} className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm bg-white">
                  <option value="">— Select —</option>
                  <option value="Skilled">Skilled</option>
                  <option value="Semi-skilled">Semi-skilled</option>
                  <option value="Unskilled">Unskilled</option>
                  {meta.skill_grade && !["Skilled","Semi-skilled","Unskilled"].includes(meta.skill_grade) && <option value={meta.skill_grade}>{meta.skill_grade} (legacy)</option>}
                </select>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Date of hire</label>
                <input type="date" value={meta.date_of_hire || meta.join_date || ""} onChange={(e) => { updateEmpMeta("date_of_hire", e.target.value); updateEmpMeta("join_date", e.target.value); }} className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Monthly Salary (₹)</label>
                <input type="number" step="1" value={meta.monthly_basic_salary || ""} onChange={(e) => updateEmpMeta("monthly_basic_salary", parseFloat(e.target.value) || 0)} className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Bank a/c number</label>
                <input data-testid="edit-master-emp-account" value={meta.bank_account || ""} onChange={(e) => updateEmpMeta("bank_account", e.target.value)} className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono" />
              </div>
              <div className="col-span-1 flex items-center gap-2 pt-1">
                <input id="edit-emp-esi-elig" type="checkbox" data-testid="edit-master-emp-esi-eligible"
                  checked={!!meta.esi_eligible}
                  onChange={(e) => updateEmpMeta("esi_eligible", e.target.checked)}
                  className="h-4 w-4 border-slate-400" />
                <label htmlFor="edit-emp-esi-elig" className="text-xs font-semibold text-slate-700">
                  Eligible for ESI
                </label>
              </div>
              <div className="col-span-1 flex items-center gap-2 pt-1">
                <input id="edit-emp-pf-elig" type="checkbox" data-testid="edit-master-emp-pf-eligible"
                  checked={!!meta.pf_eligible}
                  onChange={(e) => updateEmpMeta("pf_eligible", e.target.checked)}
                  className="h-4 w-4 border-slate-400" />
                <label htmlFor="edit-emp-pf-elig" className="text-xs font-semibold text-slate-700">
                  Eligible for PF
                </label>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Phone</label>
                <input value={meta.phone || ""} onChange={(e) => updateEmpMeta("phone", e.target.value)} className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono" />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Email</label>
                <input value={meta.email || ""} onChange={(e) => updateEmpMeta("email", e.target.value)} className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm" />
              </div>
              <div className="col-span-2 flex items-center gap-2 pt-1">
                <input id="edit-emp-ot" type="checkbox" data-testid="edit-master-emp-ot"
                  checked={!!meta.ot_eligible}
                  onChange={(e) => updateEmpMeta("ot_eligible", e.target.checked)}
                  className="h-4 w-4 border-slate-400" />
                <label htmlFor="edit-emp-ot" className="text-xs font-semibold text-slate-700">
                  OT eligible — this employee is paid for overtime hours beyond shift end
                </label>
              </div>
              <div className="col-span-2 flex items-center gap-2 pt-1">
                <input id="edit-emp-l1" type="checkbox" data-testid="edit-master-emp-l1"
                  checked={!!meta.is_l1_approver}
                  onChange={(e) => updateEmpMeta("is_l1_approver", e.target.checked)}
                  className="h-4 w-4 border-slate-400" />
                <label htmlFor="edit-emp-l1" className="text-xs font-semibold text-slate-700">
                  Mark as L1 leave approver (supervisor dropdown in the Leave form)
                </label>
              </div>
            </div>
          )}
        </fieldset>
        {isEmployee && canKyc && (
          <div className="grid grid-cols-2 gap-2 mt-3">
            <EmployeeKycFields meta={meta} onChange={updateEmpMeta} isNew={false} isDirector={isDirector} pendingFields={master.kyc_pending_fields || []} prefix="edit-emp" />
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button data-testid="edit-master-submit" onClick={() => onSave({ name, code, notes, active, meta })} disabled={submitting || !name.trim()} className="bg-slate-900 hover:bg-slate-800 text-white">
            {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : <><Save className="h-4 w-4 mr-1.5" /> Save</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

const Masters = () => {
  const { user, hasModule } = useAuth();
  const isDirector = user?.role === "director";
  const qc = useQueryClient();
  const visibleTabs = useMemo(
    () => MASTER_TABS.filter((t) => hasModule(`masters.${t.key}`)),
    [hasModule],
  );
  const [activeTab, setActiveTab] = useState(visibleTabs[0]?.key || "rm_grade");
  useEffect(() => {
    if (visibleTabs.length && !visibleTabs.some((t) => t.key === activeTab)) {
      setActiveTab(visibleTabs[0].key);
    }
  }, [visibleTabs, activeTab]);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewingEmployee, setViewingEmployee] = useState(null);  // opens read-only detail card
  const canEditEmployee = hasModule("masters.employee_edit");
  const canKyc = isDirector || hasModule("masters.employee_kyc");

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["masters", activeTab],
    queryFn: () => listMasters({ master_type: activeTab }),
  });

  const pendingCount = useMemo(() => items.filter((m) => m.status === "pending").length, [items]);
  const sorted = useMemo(() => {
    const order = { pending: 0, active: 1, rejected: 2, inactive: 3 };
    return [...items].sort((a, b) => (order[a.status] ?? 9) - (order[b.status] ?? 9) || a.name.localeCompare(b.name));
  }, [items]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["masters"] });

  const createMut = useMutation({
    mutationFn: (p) => createMaster({ master_type: activeTab, ...p }),
    onSuccess: (d) => {
      toast.success(d.status === "pending" ? "Proposal sent for director approval" : `Added ${d.name}`);
      setShowAdd(false);
      invalidate();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const editMut = useMutation({
    mutationFn: ({ id, payload }) => updateMaster(id, payload),
    onSuccess: (d) => {
      if (d?.kyc_pending_fields?.length) toast.info("Saved. Changes to filled KYC fields were sent to the Director for approval");
      else toast.success("Updated");
      setEditing(null);
      invalidate();
      qc.invalidateQueries({ queryKey: ["kyc-requests"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const approveMut = useMutation({
    mutationFn: (id) => approveMaster(id),
    onSuccess: () => { toast.success("Approved"); invalidate(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const rejectMut = useMutation({
    mutationFn: (id) => rejectMaster(id),
    onSuccess: () => { toast.success("Rejected"); invalidate(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const [empDelete, setEmpDelete] = useState(null);   // employee master doc pending confirm

  const deleteMut = useMutation({
    mutationFn: ({ id, relieved_on }) => deleteMaster(id, { relieved_on }),
    onSuccess: (data) => {
      if (data?.status === "pending_approval") {
        toast.info("Deletion pending director approval");
      } else {
        toast.success("Deleted");
      }
      invalidate();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const seedMut = useMutation({
    mutationFn: seedMastersFromExisting,
    onSuccess: (d) => {
      const created = Object.entries(d.created || {}).map(([k, v]) => `${k}: ${v.length}`).join(", ");
      toast.success(`Seeded: ${created} · normalised ${d.normalised_records} records`);
      invalidate();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Seed failed"),
  });

  return (
    <div data-testid="masters-page" className="max-w-6xl mx-auto space-y-5">
      <div className="border-b border-slate-200 pb-4 flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Database className="h-7 w-7 text-slate-900" />
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-slate-500 font-semibold">Reference data</p>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Masters</h1>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href={bridgeDownloadUrl()}
            data-testid="download-lan-print-kit"
            title="Downloads the tiny one-time setup ZIP for the LAN print relay (any LAN PC)"
            className="inline-flex items-center h-9 px-3 rounded-sm border border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-900 text-sm font-semibold"
          >
            <Printer className="h-4 w-4 mr-1.5" /> LAN Bridge Kit (Printer + Scale)
          </a>
          {isDirector && (
            <Button data-testid="seed-masters-btn" onClick={() => seedMut.mutate()} disabled={seedMut.isPending} variant="outline">
              {seedMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4 mr-1.5" />} Seed from existing data
            </Button>
          )}
          <Button data-testid="add-master-btn" onClick={() => setShowAdd(true)} className="bg-slate-900 hover:bg-slate-800 text-white">
            <Plus className="h-4 w-4 mr-1.5" /> Add {MASTER_TABS.find((t) => t.key === activeTab)?.label || activeTab}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {visibleTabs.map((t) => (
          <button
            key={t.key}
            data-testid={`tab-${t.key}`}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 h-10 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              activeTab === t.key ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            {t.label}
            {activeTab === t.key && pendingCount > 0 && (
              <span data-testid="pending-count" className="ml-2 inline-flex items-center justify-center h-5 min-w-5 px-1.5 text-[10px] font-bold rounded-full bg-amber-500 text-white">{pendingCount}</span>
            )}
          </button>
        ))}
      </div>

      {isLoading && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}

      {(activeTab === "rm_grade" || activeTab === "sfg_sku") && <WeighToleranceBar kind={activeTab === "rm_grade" ? "rm" : "sfg"} canEdit={user?.role === "director"} />}
      {(activeTab === "sfg_sku" || activeTab === "fg_sku") && <DispatchToleranceCard />}
      {activeTab === "employee" && (
        <>
          <EmployeeExcelBar canKyc={canKyc} />
          {canKyc && <KycRequestsPanel isDirector={isDirector} />}
        </>
      )}

      {!isLoading && sorted.length === 0 && (
        <div data-testid="masters-empty" className="bg-white border border-dashed border-slate-300 rounded-sm py-14 text-center text-sm text-slate-500">
          No {MASTER_TABS.find((t) => t.key === activeTab)?.label?.toLowerCase() || activeTab}s yet. {isDirector && "Click \"Seed from existing data\" to auto-populate from your current records."}
        </div>
      )}

      {!isLoading && sorted.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">
                <th className="px-3 py-2">Name</th>
                {activeTab === "fg_sku" && <th className="px-3 py-2">Grade</th>}
                {activeTab === "rm_grade" && <>
                  <th className="px-3 py-2">Grade</th>
                  <th className="px-3 py-2">Thickness (mm)</th>
                </>}
                {activeTab === "sfg_sku" && <>
                  <th className="px-3 py-2">Grade</th>
                  <th className="px-3 py-2">Thickness (mm)</th>
                  <th className="px-3 py-2">Width (mm)</th>
                </>}
                {activeTab !== "rm_grade" && activeTab !== "sfg_sku" && activeTab !== "fg_sku" && (
                  <th className="px-3 py-2">Code</th>
                )}
                <th className="px-3 py-2">Status</th>
                {activeTab === "employee" && (
                  <>
                    <th className="px-3 py-2">Date of Joining</th>
                    <th className="px-3 py-2">Date of Relieving</th>
                  </>
                )}
                <th className="px-3 py-2">Proposed by</th>
                <th className="px-3 py-2">Created</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((m) => (
                <MasterRow
                  key={m.id}
                  m={m}
                  masterType={activeTab}
                  isDirector={isDirector}
                  canEditEmployee={canEditEmployee}
                  canKyc={canKyc}
                  canDeleteEmployee={!!user?.can_delete_masters_direct || hasModule("masters.employee_delete")}
                  onApprove={(id) => approveMut.mutate(id)}
                  onReject={(id) => rejectMut.mutate(id)}
                  onDelete={(id, name) => {
                    if (activeTab === "employee") {
                      const doc = sorted.find((x) => x.id === id);
                      setEmpDelete(doc || { id, name });
                      return;
                    }
                    if (window.confirm(`Delete ${activeTab} "${name}"? Existing records that use this name still work, but you can't add it back with the same name (unless you re-create).`)) deleteMut.mutate({ id });
                  }}
                  onEdit={setEditing}
                  onOpen={setViewingEmployee}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AddDialog
        open={showAdd}
        onClose={() => setShowAdd(false)}
        masterType={activeTab}
        isDirector={isDirector}
        canKyc={canKyc}
        onSubmit={(p) => createMut.mutate(p)}
        submitting={createMut.isPending}
      />

      {editing && (
        <EditDialog
          master={editing}
          onClose={() => setEditing(null)}
          onSave={(payload) => editMut.mutate({ id: editing.id, payload })}
          submitting={editMut.isPending}
          canKyc={canKyc}
          isDirector={isDirector}
          kycOnly={editing.master_type === "employee" && !isDirector && !user?.can_edit_masters_direct && !canEditEmployee}
        />
      )}
      {viewingEmployee && (
        <EmployeeDetailDialog
          master={viewingEmployee}
          onClose={() => setViewingEmployee(null)}
          canEdit={isDirector || canEditEmployee || canKyc}
          canKyc={canKyc}
          onEdit={(m) => { setViewingEmployee(null); setEditing(m); }}
        />
      )}
      {empDelete && (
        <EmployeeDeleteWarning
          employee={empDelete}
          isDirector={isDirector}
          onCancel={() => setEmpDelete(null)}
          onConfirm={(relieved_on) => { deleteMut.mutate({ id: empDelete.id, relieved_on }); setEmpDelete(null); }}
          submitting={deleteMut.isPending}
        />
      )}
    </div>
  );
};

// ---- Employee delete warning dialog (iteration 58 + relieving-date step 59)
function EmployeeDeleteWarning({ employee, isDirector, onCancel, onConfirm, submitting }) {
  const today = new Date();
  const iso = today.toISOString().slice(0, 10);
  const dd = String(today.getDate()).padStart(2, "0");
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const yyyy = today.getFullYear();
  const ddmmyyyy = `${dd}/${mm}/${yyyy}`;
  const nextMonthLabel = (() => {
    const d = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    return `${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  })();
  const [step, setStep] = useState("warn");            // "warn" | "date"
  const [relievedOn, setRelievedOn] = useState(iso);

  return (
    <div onClick={onCancel} className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" data-testid="employee-delete-warning">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-white rounded-sm border-2 border-red-300 shadow-xl">
        <div className="border-b border-slate-200 bg-red-50 px-4 py-3">
          <p className="text-[10px] tracking-[0.3em] uppercase text-red-800 font-semibold">Warning</p>
          <h3 className="font-heading text-lg font-bold text-red-900">
            {step === "warn" ? "Delete Employee?" : "Date of Relieving"}
          </h3>
        </div>

        {step === "warn" && (
          <div className="p-4 space-y-3 text-sm text-slate-800">
            <p className="font-semibold text-slate-900" data-testid="delete-warning-name">{employee?.name}</p>
            <p>
              You are deleting this employee from the Employee Master on <b>{ddmmyyyy}</b>. They will
              be removed from the Salary Calculator for the month of <b>{nextMonthLabel}</b> onwards.
            </p>
            <p>
              The employee will remain in the current month's Salary Calculator so you can pay them
              for the days they worked. Please ensure their salary record has been maintained in the
              Register before deleting.
            </p>
            {!isDirector && (
              <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5 text-xs">
                Note: Your deletion will be sent to a director for approval before it takes effect.
              </p>
            )}
          </div>
        )}

        {step === "date" && (
          <div className="p-4 space-y-3 text-sm text-slate-800">
            <p className="font-semibold text-slate-900">{employee?.name}</p>
            <p className="text-[12px] text-slate-600">
              Enter the employee's <b>Date of Relieving</b> (last working day). The Salary Calculator
              keeps them visible until the end of the relieving month and removes them from every
              subsequent month.
            </p>
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Date of Relieving</label>
              <Input
                type="date"
                value={relievedOn}
                max={iso}
                onChange={(e) => setRelievedOn(e.target.value)}
                data-testid="delete-relieved-on"
                className="mt-1 h-10 font-mono"
              />
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-slate-100 px-4 py-3">
          <Button variant="outline" onClick={onCancel} disabled={submitting} data-testid="delete-warning-cancel">Cancel</Button>
          {step === "warn" ? (
            <Button
              className="bg-red-700 hover:bg-red-800 text-white"
              onClick={() => setStep("date")}
              data-testid="delete-warning-confirm"
            >
              {isDirector ? "Proceed with Deletion" : "Continue"}
            </Button>
          ) : (
            <Button
              className="bg-red-700 hover:bg-red-800 text-white"
              onClick={() => onConfirm(relievedOn)}
              disabled={submitting || !relievedOn}
              data-testid="delete-date-confirm"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (isDirector ? "Confirm & Delete" : "Request Deletion")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}


export default Masters;
